<template>
  <div class="min-h-dvh bg-[#13191d] text-white transition-colors duration-300">
    <!-- Header -->
    <header class="bg-black/30">
      <div class="mx-auto pl-6 pr-2">
        <div class="flex justify-between items-center h-10">
          <div class="flex items-center gap-1">
            <div>
              <h1 class="text-lg font-semibold text-white">Monitor</h1>
            </div>
          </div>

          <div class="flex items-center gap-1">
            <!-- <button
              @click="toggleTestMode"
              :class="[
                'inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white border border-transparent rounded-xs focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors',
                useTestPath ? 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500' : 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500'
              ]"
              :title="useTestPath ? 'Disable Test Path (mv/...)' : 'Enable Test Path (mv/...)'"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3">
                <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              {{ useTestPath ? 'Test Mode ON' : 'Test Mode OFF' }}
            </button> -->

            <button
              @click="addMonitorRow"
              class="inline-flex items-center justify-center w-8 h-8 text-white bg-yellow-600 border border-transparent rounded-xs hover:bg-yellow-700 focus:outline-hidden focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors"
              title="Add Monitor Row"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
                <path
                  d="M12 2.25c.414 0 .75.336.75.75v8.25h8.25a.75.75 0 010 1.5h-8.25v8.25a.75.75 0 01-1.5 0v-8.25H3a.75.75 0 010-1.5h8.25V3a.75.75 0 01.75-.75z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Monitor Rows -->
    <div class="p-4 space-y-4">
      <div v-for="row in monitorRows" :key="row.id" class="w-full">
        <MonitorRow
          :row-id="row.id"
          :input-sources="inputSources"
          :can-remove="monitorRows.length > 1"
          :thumbnail-provider="thumbnailProvider"
          @remove-row="removeMonitorRow"
        />
      </div>

      <!-- Empty State -->
      <div v-if="monitorRows.length === 0" class="text-center py-20">
        <div class="text-gray-400">
          <svg class="mx-auto h-12 w-12 mb-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <h3 class="text-lg font-medium mb-2 text-white">No monitor rows added</h3>
          <p class="text-sm text-gray-300">Click the + button to add your first monitor row</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance, onBeforeUnmount } from "vue";
import MonitorRow from "@/components/Monitor/MonitorRow.vue";

// Get access to the Vue instance and store
const instance = getCurrentInstance();

// Reactive data
const monitorRows = ref([]);
const inputSources = ref([]);
const useTestPath = ref(true);
let rowIdCounter = 0;

// Thumbnail management - similar to RouterAlt.vue
const inputThumbnails = ref({});
const thumbnailRefreshInterval = ref(null);
const thumbnailRefreshDelay = 15000; // 15 seconds in milliseconds
const thumbnailBatchLoading = ref(false);
const lastThumbnailFetch = ref(null);

const detectMonitorSourceType = (input) => {
  const protocolFromConfig = input?.protocolConfiguration?.protocol
    || input?.protocolConfiguration?.Protocol
    || input?.protocol
    || input?.mcInputProtocol;
  if (protocolFromConfig) {
    return String(protocolFromConfig).toUpperCase();
  }

  const rawUrl = (input?.url || "").trim();
  const lowerUrl = rawUrl.toLowerCase();

  if (!lowerUrl) {
    return (input?.type || "INPUT").toUpperCase();
  }

  if (lowerUrl.startsWith("srt://")) {
    if (/[?&]mode=caller(?:&|$)/i.test(rawUrl)) return "SRT_CALLER";
    if (/[?&]mode=listener(?:&|$)/i.test(rawUrl)) return "SRT_LISTENER";
    return "SRT";
  }

  if (lowerUrl.startsWith("udp://")) return "UDP";
  if (lowerUrl.startsWith("rtp://")) return "RTP";
  if (lowerUrl.startsWith("rist://")) return "RIST";
  if (lowerUrl.includes("/whep")) return "WHEP";

  return (input?.type || "INPUT").toUpperCase();
};

// Convert UDP URL to WebRTC WHEP format
const convertUdpToWebRtc = (originalUrl) => {
  try {
    // Check if URL is already in WebRTC format
    if (originalUrl.includes('webrtc') && originalUrl.includes('whep')) {
      return originalUrl;
    }

    // Parse UDP URL (e.g., udp://239.1.125.104:10104)
    const url = new URL(originalUrl);
    
    if (url.protocol === 'udp:') {
      const ip = url.hostname;
      const port = url.port;
      
      // Convert to WebRTC format
      // const webrtcUrl = `https://monitor.elas.tech/webrtc/adm/${ip}/${port}/whep`;
      const webrtcUrl = `https://monitor.elas.tech/mv/${ip}/${port}/whep`;
      console.log(`Converted UDP URL: ${originalUrl} → ${webrtcUrl}`);
      return webrtcUrl;
    }
    
    // If not UDP, return original URL
    return originalUrl;
  } catch (error) {
    console.warn(`Failed to convert URL: ${originalUrl}`, error);
    return originalUrl;
  }
};

// Convert UDP URL to new MV format (test path)
const convertUdpToMvFormat = (originalUrl) => {
  try {
    // Parse UDP URL (e.g., udp://239.61.13.1:10120)
    const url = new URL(originalUrl);
    
    if (url.protocol === 'udp:') {
      const ip = url.hostname;
      const port = url.port;
      
      // Extract last two octets from IP (e.g., 239.61.13.1 → 13.1)
      const ipParts = ip.split('.');
      if (ipParts.length === 4) {
        const lastTwoOctets = ipParts.slice(2).join('.');
        
        // Build new format: mv/{lastTwoOctets}/{port} - a1 will be added by child component
        const mvUrl = `https://monitor.elas.tech/mv/${lastTwoOctets}/${port}/whep`;
        console.log(`Converted to MV format: ${originalUrl} → ${mvUrl}`);
        return mvUrl;
      }
    }
    
    // If not UDP or invalid format, return original URL
    return originalUrl;
  } catch (error) {
    console.warn(`Failed to convert URL to MV format: ${originalUrl}`, error);
    return originalUrl;
  }
};

// Prepare input sources from store
const prepareInputSources = () => {
  const inputs = instance.proxy.$store.state.permissions.inputs;
  if (!inputs) {
    console.log("No inputs found in store");
    return;
  }

  console.log("Raw inputs from store:", inputs);

  // Filter inputs with non-null URL and prepare them as sources
  const validInputs = inputs.filter(input => input.url && input.url.trim() !== '');
  
  inputSources.value = validInputs.map(input => {
    const originalUrl = input.url;
    
    // Choose conversion method based on test mode
    const convertedUrl = useTestPath.value 
      ? convertUdpToMvFormat(originalUrl)
      : convertUdpToWebRtc(originalUrl);
    
    const pathType = useTestPath.value ? 'mv/...' : 'webrtc/adm/...';
    
    return {
      id: input.id,
      name: `${input.name || input.description || `Input ${input.id}`} `,
      url: convertedUrl,
      originalUrl: originalUrl, // Keep original for reference
      type: 'input',
      sourceType: detectMonitorSourceType(input),
      status: input.status,
      description: input.description,
      probeProjectId: input.probeProjectId,
      probeTaskId: input.probeTaskId,
      pathType: pathType
    };
  });

  console.log(`Prepared ${inputSources.value.length} input sources from ${inputs.length} total inputs using ${useTestPath.value ? 'MV' : 'WebRTC'} format:`, inputSources.value);
};

// Toggle test mode function
const toggleTestMode = () => {
  useTestPath.value = !useTestPath.value;
  console.log(`Test mode ${useTestPath.value ? 'enabled' : 'disabled'} - using ${useTestPath.value ? 'mv/...' : 'webrtc/adm/...'} path format`);
  
  // Re-prepare input sources with new path format
  prepareInputSources();
};

// Thumbnail Management Methods (similar to RouterAlt.vue)
/**
 * Initialize thumbnail refresh interval
 * Fetches thumbnails for all inputs every 15 seconds using batch API
 */
const initThumbnailRefresh = () => {
  // Initial thumbnail fetch
  fetchInputThumbnails();

  // Set up recurring thumbnail refresh every 15 seconds
  thumbnailRefreshInterval.value = setInterval(() => {
    fetchInputThumbnails();
  }, thumbnailRefreshDelay);
};

/**
 * Fetch thumbnails for all inputs using batch API
 * Optimized to avoid excessive network requests
 */
const fetchInputThumbnails = async () => {
  if (!inputSources.value || inputSources.value.length === 0) return;
  
  // Prevent overlapping batch requests
  if (thumbnailBatchLoading.value) {
    console.log('Thumbnail batch request already in progress, skipping...');
    return;
  }

  thumbnailBatchLoading.value = true;
  lastThumbnailFetch.value = new Date();

  try {
    const probeInputs = [];
    const routerInputs = [];

    inputSources.value.forEach(input => {
      // Inputs with probeProjectId and probeTaskId are probe inputs
      if (input.probeProjectId && input.probeTaskId) {
        probeInputs.push(input);
      } else {
        // All other inputs are treated as router inputs
        routerInputs.push(input);
      }
    });

    const promises = [];

    // 1. Fetch Probe Thumbnails
    if (probeInputs.length > 0) {
      const probeTask = async () => {
        try {
          // Prepare batch request data for probe inputs
          const projects = probeInputs.map(input => ({
            projectId: input.probeProjectId,
            taskId: input.probeTaskId
          }));

          // Import Servers service
          const Servers = (await import('@/services/Servers')).default; // Dynamic import if needed, or stick to existing pattern

          // Make batch request to inputThumbs endpoint
          const response = await Servers.getInputThumbs({
            projects: projects
          });

          if (response?.data) {
            // Process batch response and update thumbnail data
            processBatchThumbnailResponse(response.data);
            console.log('Probe thumbnail batch response processed:', response.data);
          } else {
            console.log('No probe thumbnail data in response:', response?.data);
          }
        } catch (error) {
          console.error('Error fetching probe thumbnails:', error);
          // Mark probe inputs as having errors
          probeInputs.forEach(input => {
            inputThumbnails.value[input.id] = {
              data: null,
              timestamp: new Date(),
              loading: false,
              error: 'Failed to fetch thumbnail'
            };
          });
        }
      };
      promises.push(probeTask());
    }

    // 2. Fetch Router Thumbnails
    if (routerInputs.length > 0) {
      const routerTask = async () => {
        try {
          const inputIds = routerInputs.map(i => i.id);
          // Dispatch action to fetch router thumbnails
          const thumbs = await instance.proxy.$store.dispatch('routerAlt/fetchRouterThumbnails', inputIds);
          processRouterThumbnailResponse(thumbs);
        } catch (error) {
           console.error('Error fetching router thumbnails:', error);
           // Mark router inputs as having errors
           routerInputs.forEach(input => {
            inputThumbnails.value[input.id] = {
              data: null,
              timestamp: new Date(),
              loading: false,
              error: 'Failed to fetch thumbnail'
            };
          });
        }
      };
      promises.push(routerTask());
    }

    await Promise.all(promises);

  } catch (error) {
    console.error('Error in fetchInputThumbnails:', error);
  } finally {
    thumbnailBatchLoading.value = false;
  }
};

/**
 * Process router thumbnail response and update thumbnail data
 * @param {Object} thumbsData - Response data from router thumbnail API
 */
const processRouterThumbnailResponse = (thumbsData) => {
  if (!thumbsData || typeof thumbsData !== 'object') return;
  
  let updatedCount = 0;

  Object.entries(thumbsData).forEach(([inputId, base64Thumb]) => {
     const currentThumbnail = inputThumbnails.value[inputId];
     // Check if data changed
     const hasNewData = base64Thumb && base64Thumb !== currentThumbnail?.data;

     if (base64Thumb) {
         if (hasNewData || !currentThumbnail) {
             inputThumbnails.value[inputId] = {
                 data: base64Thumb,
                 timestamp: new Date(),
                 loading: false,
                 error: null
             };
             updatedCount++;
         }
     } else if (!currentThumbnail) {
         inputThumbnails.value[inputId] = {
             data: null,
             timestamp: new Date(),
             loading: false,
             error: null
        };
     }
  });

  if (updatedCount > 0) {
    console.log(`Router thumbnails processed: ${updatedCount} thumbnails updated`);
  }
};

/**
 * Process batch thumbnail response and update thumbnail data
 * Only updates and logs when there's new thumbnail data
 * @param {Object} thumbsData - Response data from batch API
 */
const processBatchThumbnailResponse = (thumbsData) => {
  if (!thumbsData || typeof thumbsData !== 'object') return;

  let updatedCount = 0;

  inputSources.value.forEach(input => {
    const inputKey = input.probeProjectId || input.id;
    const thumbnailData = thumbsData[inputKey];
    const newThumbnailData = thumbnailData?.[input.probeTaskId]?.data;
    
    // Get current thumbnail data for comparison
    const currentThumbnail = inputThumbnails.value[input.id];
    const hasNewData = newThumbnailData && newThumbnailData !== currentThumbnail?.data;
    
    if (thumbnailData) {
      // Only update if there's actually new thumbnail data
      if (hasNewData || !currentThumbnail) {
        inputThumbnails.value[input.id] = {
          data: newThumbnailData,
          timestamp: new Date(),
          loading: false,
          error: null
        };
        updatedCount++;
        console.log(`New thumbnail data for input ${input.id} (${input.name})`);
      }
    } else if (!currentThumbnail) {
      // Only mark as unavailable if we don't have any data yet
      inputThumbnails.value[input.id] = {
        data: null,
        timestamp: new Date(),
        loading: false,
        error: null
      };
    }
  });

  if (updatedCount > 0) {
    console.log(`Thumbnail batch processed: ${updatedCount} thumbnails updated`);
  }
};

/**
 * Get thumbnail data for a specific input
 * @param {Object} input - Input object
 * @returns {Object} Thumbnail data with loading/error states
 */
const getThumbnailData = (input) => {
  const thumbnailData = inputThumbnails.value[input.id];
  
  if (!thumbnailData) {
    return {
      data: null,
      loading: thumbnailBatchLoading.value,
      error: null,
      timestamp: null
    };
  }

  return {
    data: thumbnailData.data,
    loading: thumbnailData.loading || thumbnailBatchLoading.value,
    error: thumbnailData.error,
    timestamp: thumbnailData.timestamp
  };
};

// Row management functions
const addMonitorRow = () => {
  rowIdCounter++;
  const newRow = {
    id: rowIdCounter,
    name: `Monitor ${rowIdCounter}`,
  };

  monitorRows.value.push(newRow);
  console.log("Added monitor row:", newRow);
};

const removeMonitorRow = (rowId) => {
  const index = monitorRows.value.findIndex((row) => row.id === rowId);
  if (index > -1) {
    monitorRows.value.splice(index, 1);
    console.log("Removed monitor row:", rowId);
  }
};

// Provide thumbnail functionality to child components
const provideThumbnailData = () => {
  return {
    getThumbnailData,
    inputThumbnails: inputThumbnails.value,
    thumbnailBatchLoading: thumbnailBatchLoading.value
  };
};

// Lifecycle hooks
onMounted(async () => {
  try {
    // Call listInputs when component is mounted
    await instance.proxy.$store.dispatch('permissions/listInputs');
    console.log("Successfully loaded inputs on mount");
    
    // Prepare the input sources list
    prepareInputSources();

    // Initialize thumbnail refresh
    initThumbnailRefresh();

    // Add initial monitor row
    addMonitorRow();
  } catch (error) {
    console.error("Failed to load inputs:", error);
  }
});

onBeforeUnmount(() => {
  if (thumbnailRefreshInterval.value) {
    clearInterval(thumbnailRefreshInterval.value);
    thumbnailRefreshInterval.value = null;
  }
});

// Provide data to child components
const provideData = () => {
  return {
    getThumbnailData,
    inputThumbnails: inputThumbnails.value,
    thumbnailBatchLoading: thumbnailBatchLoading.value
  };
};

// Watch for updates to provide reactive data
const thumbnailProvider = computed(() => provideData());
</script>

<style scoped>
/* Component styles */
</style>
